import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Form,
  InputGroup,
  Spinner,
  Button,
  Modal,
} from "react-bootstrap";
import {
  BsBagCheck,
  BsSearch,
} from "react-icons/bs";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../shared/styles/UtillitiesComponent.css";

import { auth, db } from "../../shared/libs/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import InstallmentCourseEntryGuard from "../../shared/components/InstallmentCourseEntryGuard";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function courseMatchesCategory(course, selectedCategory) {
  if (!selectedCategory || selectedCategory === "all") return true;

  const categories = Array.isArray(course?.categories) ? course.categories : [];
  const courseType = String(course?.courseType || "").trim();

  if (categories.includes(selectedCategory)) return true;

  const mapCourseTypeToCategory = {
    thaibar: "เนติบัณฑิต",
    attorney: "ทนายความ",
    prosecutor: "อัยการ",
    judge: "ผู้พิพากษา",
    ocsc: "สอบ ก.พ.",
  };

  return mapCourseTypeToCategory[courseType] === selectedCategory;
}

function purchaseMatchesStatus(row, statusFilter) {
  if (statusFilter === "all") return true;
  if (statusFilter === "paid") return row?.paymentStatus === "paid";
  if (statusFilter === "pending") return row?.paymentStatus !== "paid";
  return true;
}

function getPaymentBadge(paymentStatus, paymentType) {
  if (paymentStatus === "paid") {
    return {
      label: "สมัครแล้ว",
      className: "text-bg-success",
    };
  }

  if (paymentType === "installment" && paymentStatus === "partial_paid") {
    return {
      label: "ผ่อนชำระอยู่",
      className: "text-bg-info text-dark",
    };
  }

  return {
    label: "รอการชำระเงิน",
    className: "text-bg-warning text-dark",
  };
}

function getCourseActiveBadge(isActive) {
  if (isActive) {
    return {
      label: "พร้อมใช้งาน",
      className: "text-bg-success",
    };
  }

  return {
    label: "หมดอายุ",
    className: "text-bg-secondary",
  };
}

async function fetchMyEnrollments(uid, maxFetch = 100) {
  const q1 = query(
    collection(db, "enrolls"),
    where("userId", "==", uid),
    orderBy("createdAt", "desc"),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q1);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyEnrollments q1 failed:", e?.message);
  }

  const q2 = query(
    collection(db, "enrolls"),
    where("uid", "==", uid),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q2);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyEnrollments q2 failed:", e?.message);
  }

  return [];
}

async function fetchCourseDoc(courseId) {
  try {
    const ref = doc(db, "courses", courseId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.warn("fetchCourseDoc failed:", e?.message);
    return null;
  }
}

function getInstallmentList(row) {
  return Array.isArray(row?.installments) ? row.installments : [];
}

function getPaidInstallmentCount(row) {
  return getInstallmentList(row).filter((x) => x?.status === "paid").length;
}

function getTotalInstallmentCount(row) {
  return getInstallmentList(row).length || Number(row?.installmentPlan?.totalInstallments || 0);
}

function hasRemainingInstallments(row) {
  if (row?.paymentType !== "installment") return false;
  return getInstallmentList(row).some((x) => x?.status !== "paid");
}

function getCurrentInstallment(row) {
  if (row?.paymentType !== "installment") return null;
  return getInstallmentList(row).find((x) => x?.status !== "paid") || null;
}

export default function MyCoursePurchases() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const [keyword, setKeyword] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    let alive = true;

    (async () => {
        try {
        setLoading(true);
        setError("");

        const uid = auth?.currentUser?.uid;
        if (!uid) {
            if (!alive) return;
            setRows([]);
            return;
        }

        const enrolls = await fetchMyEnrollments(uid, 100);

        const data = await Promise.all(
            enrolls.map(async (enroll) => {
            const courseId = enroll?.courseId;
            if (!courseId) return null;

            const course = await fetchCourseDoc(courseId);

            const courseTitle =
                course?.title ||
                enroll?.courseTitle ||
                enroll?.courseSnapshot?.title ||
                "คอร์สเรียน";

            const coverImage =
                course?.imageCoverUrl ||
                enroll?.courseSnapshot?.coverImage ||
                "";

            return {
                id: enroll.id,
                enrollId: enroll.id,
                userSnapshot: enroll?.userSnapshot || null,
                applicantInfoEditedAt: enroll?.applicantInfoEditedAt || null,
                courseId,
                slug: course?.slug || enroll?.courseSlug || courseId,
                title: courseTitle,
                subTitle: course?.subTitle || "",
                imageCoverUrl: coverImage,
                categories: Array.isArray(course?.categories) ? course.categories : [],
                courseType: course?.courseType || enroll?.courseType || "",
                isActive: course?.isActive !== false,

                paymentStatus: enroll?.paymentStatus || "pending",
                accessStatus: enroll?.accessStatus || "",
                paymentType: enroll?.paymentType || "full",

                amount: Number(enroll?.amount || 0),
                amountText: enroll?.amountText || "",
                coursePackage: enroll?.coursePackage || "",
                coursePackageLabel: enroll?.coursePackageLabel || "",
                paymentMethod: enroll?.paymentMethod || "",
                paymentChannel: enroll?.paymentChannel || "",
                purchaseMode: enroll?.purchaseMode || "",

                installmentPlan: enroll?.installmentPlan || null,
                installments: Array.isArray(enroll?.installments) ? enroll.installments : [],

                createdAt: enroll?.createdAt || null,
                paidAt: enroll?.paidAt || null,
                updatedAt: enroll?.updatedAt || null,
                
              };
            })
        );

        const cleanRows = data.filter(Boolean);

        // --------------------------------------------------
        // dedupe rules
        // - thaibar       -> courseId + coursePackage + purchaseMode
        // - other courses -> courseId + purchaseMode
        //
        // keep rule:
        // 1) if same group has paid rows -> keep latest paid row
        // 2) if no paid row -> keep latest row
        // --------------------------------------------------
        const grouped = new Map();

        for (const row of cleanRows) {
            const courseType = String(row?.courseType || "").trim().toLowerCase();
            const courseId = String(row?.courseId || "").trim();
            const coursePackage = String(row?.coursePackage || "").trim();
            const purchaseMode = String(row?.purchaseMode || "").trim();

            const groupKey =
            courseType === "thaibar"
                ? [courseId, coursePackage, purchaseMode].join("::")
                : [courseId, purchaseMode].join("::");

            if (!grouped.has(groupKey)) {
            grouped.set(groupKey, []);
            }

            grouped.get(groupKey).push(row);
        }

        const pickLatest = (rows) => {
            return [...rows].sort((a, b) => {
            const aPaid = toDateSafe(a?.paidAt)?.getTime() || 0;
            const bPaid = toDateSafe(b?.paidAt)?.getTime() || 0;
            if (aPaid !== bPaid) return bPaid - aPaid;

            const aUpdated = toDateSafe(a?.updatedAt)?.getTime() || 0;
            const bUpdated = toDateSafe(b?.updatedAt)?.getTime() || 0;
            if (aUpdated !== bUpdated) return bUpdated - aUpdated;

            const aCreated = toDateSafe(a?.createdAt)?.getTime() || 0;
            const bCreated = toDateSafe(b?.createdAt)?.getTime() || 0;
            return bCreated - aCreated;
            })[0];
        };

        const dedupedRows = Array.from(grouped.values()).map((groupRows) => {
            const paidRows = groupRows.filter((r) => r?.paymentStatus === "paid");

            if (paidRows.length > 0) {
            return pickLatest(paidRows);
            }

            return pickLatest(groupRows);
        });

        dedupedRows.sort((a, b) => {
            const aPaid = toDateSafe(a?.paidAt)?.getTime() || 0;
            const bPaid = toDateSafe(b?.paidAt)?.getTime() || 0;
            if (aPaid !== bPaid) return bPaid - aPaid;

            const aUpdated = toDateSafe(a?.updatedAt)?.getTime() || 0;
            const bUpdated = toDateSafe(b?.updatedAt)?.getTime() || 0;
            if (aUpdated !== bUpdated) return bUpdated - aUpdated;

            const aCreated = toDateSafe(a?.createdAt)?.getTime() || 0;
            const bCreated = toDateSafe(b?.createdAt)?.getTime() || 0;
            return bCreated - aCreated;
        });

        if (!alive) return;
        setRows(dedupedRows);
        } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "โหลดข้อมูลการซื้อคอร์สเรียนไม่สำเร็จ");
        } finally {
        if (alive) setLoading(false);
        }
    })();

    return () => {
        alive = false;
    };
    }, []);

  const categoryOptions = useMemo(() => {
    const set = new Set(["all"]);

    for (const row of rows) {
      const cats = Array.isArray(row?.categories) ? row.categories : [];
      cats.forEach((c) => {
        const v = String(c || "").trim();
        if (v) set.add(v);
      });
    }

    return Array.from(set);
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = keyword.trim().toLowerCase();

    return rows.filter((row) => {
      const hitKeyword =
        !q ||
        String(row?.title || "").toLowerCase().includes(q) ||
        String(row?.subTitle || "").toLowerCase().includes(q) ||
        String(row?.coursePackageLabel || "").toLowerCase().includes(q);

      return (
        hitKeyword &&
        courseMatchesCategory(row, selectedCategory) &&
        purchaseMatchesStatus(row, statusFilter)
      );
    });
  }, [rows, keyword, selectedCategory, statusFilter]);

  if (!auth?.currentUser) return null;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <BsBagCheck />
        <h4 className="mb-0">การซื้อคอร์สเรียน</h4>
      </div>

      <Row className="g-2 mb-3">
        <Col xs={12} lg={6}>
          <InputGroup>
            <InputGroup.Text>
              <BsSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="ค้นหาชื่อคอร์ส / แพ็คเกจ"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </InputGroup>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>
                {cat === "all" ? "ทุกหมวดหมู่" : cat}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col xs={12} md={6} lg={3}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">สถานะการซื้อทั้งหมด</option>
            <option value="paid">ชำระเงินแล้ว</option>
            <option value="pending">รอการชำระเงิน</option>
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && error && (
        <div className="text-danger">{error}</div>
      )}

      {!loading && !error && filteredRows.length === 0 && (
        <div className="text-muted">ไม่พบรายการซื้อคอร์สเรียนที่ตรงเงื่อนไข</div>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <div className="d-flex flex-column gap-3">
          {filteredRows.map((row) => {
            // const paymentBadge = getPaymentBadge(row.paymentStatus);
            const paymentBadge = getPaymentBadge(row.paymentStatus, row.paymentType);
            const activeBadge = getCourseActiveBadge(row.isActive);

            const courseForEntryGuard = {
            id: row.courseId,
            enrolls: [
                {
                id: row.enrollId,
                courseId: row.courseId,
                paymentType: row.paymentType || "full",
                installments: Array.isArray(row.installments) ? row.installments : [],
                },
            ],
            };
            

            return (
              <div
                key={row.id}
                className="card app-card rounded-4 overflow-hidden"
              >
                <Row className="g-0 align-items-stretch">
                  <Col xs={12} md={5} lg={4}>
                    <div
                      className="position-relative bg-surface w-100"
                      style={{
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                      }}
                    >
                      {row.imageCoverUrl ? (
                        <img
                          src={row.imageCoverUrl}
                          alt={row.title}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center h-100 text-secondary"
                          style={{ fontSize: 14 }}
                        >
                          ไม่มีรูปปก
                        </div>
                      )}
                    </div>
                  </Col>

                  <Col xs={12} md={7} lg={8} style={{ minWidth: 0 }}>
                    <div className="card-body p-3 p-md-4">
                      <div className="d-flex flex-column gap-3 h-100">
                        <div className="text-start" style={{ minWidth: 0 }}>
                            <div
                                className="fw-semibold text-main"
                                style={{
                                fontSize: 18,
                                lineHeight: 1.4,
                                wordBreak: "break-word",
                                }}
                            >
                                {row.title}
                            </div>

                            {row.subTitle ? (
                                <div
                                className="text-secondary small mt-1"
                                style={{ wordBreak: "break-word" }}
                                >
                                {row.subTitle}
                                </div>
                            ) : null}

                            {/* ✅ badges มาอยู่ตรงนี้ */}
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {(row.categories || []).slice(0, 2).map((cat) => (
                                <span key={cat} className="badge text-bg-light border">
                                    {cat}
                                </span>
                                ))}

                                <span className={`badge ${paymentBadge.className}`}>
                                {paymentBadge.label}
                                </span>

                                <span className={`badge ${activeBadge.className}`}>
                                {activeBadge.label}
                                </span>
                            </div>
                            </div>

                        <div className="text-start d-flex flex-column gap-2">
                          {row.coursePackageLabel ? (
                            <div>
                              <small className="text-secondary d-block">แพ็คเกจที่ซื้อ</small>
                              <div className="fw-medium">{row.coursePackageLabel}</div>
                            </div>
                          ) : null}

                          <div>
                            <small className="text-secondary d-block">ยอดชำระ</small>
                            <div className="fw-medium">
                              {row.amountText || row.amount || "-"} บาท
                            </div>

                            <button
                              type="button"
                              className="btn btn-link p-0 mt-1 text-decoration-none small"
                              onClick={() => setSelectedDetail(row)}
                            >
                              รายละเอียด
                            </button>
                          </div>

                          {row.paymentType === "installment" ? (
                            <div>
                                <small className="text-secondary d-block">การผ่อนชำระ</small>
                                <div className="fw-medium">
                                ชำระแล้ว {getPaidInstallmentCount(row)}/{getTotalInstallmentCount(row)} งวด
                                </div>
                            </div>
                            ) : null}

                            {row.paymentType === "installment" && getCurrentInstallment(row) ? (
                            <div>
                                <small className="text-secondary d-block">งวดที่ต้องชำระถัดไป</small>
                                <div className="fw-medium">
                                งวดที่ {getCurrentInstallment(row)?.installmentNo} •{" "}
                                {Number(getCurrentInstallment(row)?.amount || 0)} บาท
                                </div>
                            </div>
                            ) : null}

                          <div className="d-flex flex-wrap gap-4">
                            <div>
                              <small className="text-secondary d-block">วันที่ทำรายการ</small>
                              <div>
                                {toDateSafe(row.createdAt)
                                  ? toDateSafe(row.createdAt).toLocaleString("th-TH")
                                  : "-"}
                              </div>
                            </div>

                            <div>
                              <small className="text-secondary d-block">วันที่ชำระเงิน</small>
                              <div>
                                {toDateSafe(row.paidAt)
                                  ? toDateSafe(row.paidAt).toLocaleString("th-TH")
                                  : "-"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-auto d-flex flex-wrap gap-2">
                            {(
                            row.paymentStatus === "paid" ||
                            (row.paymentType === "installment" && row.paymentStatus === "partial_paid")
                            ) && row.isActive ? (
                            <InstallmentCourseEntryGuard
                                course={courseForEntryGuard}
                                onAllowed={() => navigate(`/learn/${row.courseId}`)}
                            >
                                {({ handleEnter }) => (
                                <Button variant="primary" onClick={handleEnter}>
                                    เข้าเรียน
                                </Button>
                                )}
                            </InstallmentCourseEntryGuard>
                            ) : row.paymentType === "installment" && hasRemainingInstallments(row) ? (
                            <Button
                                variant="warning"
                                onClick={() =>
                                navigate(`/courses/${row.courseId}/payment-promptpay/${row.enrollId}`)
                                }
                            >
                                ชำระเพิ่ม
                            </Button>
                            ) : row.paymentStatus !== "paid" ? (
                            <Button
                                variant="success"
                                onClick={() =>
                                navigate(`/courses/${row.courseId}/payment-promptpay/${row.enrollId}`)
                                }
                            >
                                ชำระเงิน
                            </Button>
                            ) : (
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(`/course/${row.slug}`)}
                            >
                                ดูรายละเอียดคอร์ส
                            </Button>
                            )}
                            </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        show={!!selectedDetail}
        onHide={() => setSelectedDetail(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>ข้อมูลการสมัคร</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedDetail ? (
            <div className="d-flex flex-column gap-3">
              <div>
                <small className="text-secondary d-block">ชื่อ</small>
                <div className="fw-medium">
                  {selectedDetail?.userSnapshot?.fullName || "-"}
                </div>
              </div>

              <div>
                <small className="text-secondary d-block">เบอร์โทร</small>
                <div className="fw-medium">
                  {selectedDetail?.userSnapshot?.phoneNumber || "-"}
                </div>
              </div>

              <div>
                <small className="text-secondary d-block">ที่อยู่จัดส่งเอกสาร</small>
                <div className="fw-medium" style={{ whiteSpace: "pre-wrap" }}>
                  {selectedDetail?.userSnapshot?.shippingAddress || "-"}
                </div>
              </div>

              <hr className="my-1" />

              <div>
                <small className="text-secondary d-block">แพ็คเกจที่ซื้อ</small>
                <div className="fw-medium">
                  {selectedDetail?.coursePackageLabel || selectedDetail?.coursePackage || "-"}
                </div>
              </div>

              <div>
                <small className="text-secondary d-block">ยอดชำระ</small>
                <div className="fw-medium">
                  {selectedDetail?.amountText || selectedDetail?.amount || "-"} บาท
                </div>
              </div>

              <div>
                <small className="text-secondary d-block">วันที่ทำรายการ</small>
                <div className="fw-medium">
                  {toDateSafe(selectedDetail?.createdAt)
                    ? toDateSafe(selectedDetail?.createdAt).toLocaleString("th-TH")
                    : "-"}
                </div>
              </div>

              <div>
                <small className="text-secondary d-block">วันที่ชำระเงิน</small>
                <div className="fw-medium">
                  {toDateSafe(selectedDetail?.paidAt)
                    ? toDateSafe(selectedDetail?.paidAt).toLocaleString("th-TH")
                    : "-"}
                </div>
              </div>

              {toDateSafe(selectedDetail?.applicantInfoEditedAt) ? (
                <div className="small text-secondary">
                  ข้อมูลผู้สมัครมีการแก้ไขล่าสุดเมื่อ{" "}
                  {toDateSafe(selectedDetail?.applicantInfoEditedAt).toLocaleString("th-TH")}
                </div>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedDetail(null)}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}